import {
  Cable as PortIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Router as RouterIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  AdminEmptyState,
  PageShell,
  AdminSectionPanel,
  AdminTablePanel,
  ErrorState,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { LoadingState } from "@/components/common/LoadingState";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { paths } from "@/routes/paths";
import { CustomerService } from "@/services/features/CustomerService";
import { RFIDReaderService } from "@/services/features/RFIDReaderService";
import { StoreService } from "@/services/features/StoreService";

interface ReaderPort {
  port: number;
  changingRoomId: number;
  rssiFilter: number;
}

interface ReaderDetails {
  name: string;
  username?: string;
  password?: string;
  debug: boolean;
  enableDeduplication: boolean;
  externalId: string;
  storeId: number;
  ports: ReaderPort[];
}

interface StoreDetails {
  id: number;
  name: string;
  customerId: number;
}

type LoadError = "reader" | "store" | "unknown";
type PortColumn = "port" | "changingRoomId" | "rssiFilter";

interface DetailItemProps {
  label: React.ReactNode;
  value: React.ReactNode;
}

const detailLabelSx = {
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

const detailsGridSx = {
  display: "grid",
  gap: 3,
  gridTemplateColumns: { md: "repeat(2, minmax(0, 1fr))", xs: "1fr" },
} as const;

const DetailItem = ({ label, value }: DetailItemProps) => (
  <Box>
    <Typography component="dt" sx={detailLabelSx}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, mt: 0.5, overflowWrap: "anywhere" }} variant="body1">
      {value}
    </Typography>
  </Box>
);

const rfidReaderService = RFIDReaderService.getInstance();
const storeService = StoreService.getInstance();
const customerService = CustomerService.getInstance();

const RFIDReaderDetails: React.FC = () => {
  const { readerName } = useParams<{ readerName: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [reader, setReader] = useState<ReaderDetails | null>(null);
  const [store, setStore] = useState<StoreDetails | null>(null);
  const [rfidUrl, setRfidUrl] = useState<string | null>(null);
  const [error, setError] = useState<LoadError | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    ReaderPort,
    PortColumn
  >(
    reader?.ports ?? [],
    (port, column) => port[column],
    "port",
    i18n.resolvedLanguage ?? i18n.language,
  );

  const loadReader = useCallback(async () => {
    if (!readerName) {
      setError("reader");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setReader(null);
    setStore(null);
    setRfidUrl(null);

    try {
      const readerResponse = await rfidReaderService.getReaderByName(readerName);
      if (!readerResponse.success || !readerResponse.data) {
        setError("reader");
        return;
      }

      const nextReader = readerResponse.data;
      setReader(nextReader);

      const storeResponse = await storeService.getStoreById(nextReader.storeId);
      if (!storeResponse.data) {
        setError("store");
        return;
      }

      const nextStore = storeResponse.data;
      setStore(nextStore);

      if (nextStore.customerId) {
        const configResponse = await customerService.getCustomerConfigByCustomerId(
          nextStore.customerId,
        );
        const configuredUrl = configResponse.data?.generalConfig?.rfidUrl;
        if (configResponse.success && configuredUrl) {
          setRfidUrl(configuredUrl.startsWith("/") ? configuredUrl : `/${configuredUrl}`);
        }
      }
    } catch {
      setError("unknown");
    } finally {
      setIsLoading(false);
    }
  }, [readerName]);

  useEffect(() => {
    void loadReader();
  }, [loadReader]);

  useEffect(() => {
    document.title = t("management.devices.rfid.readerDetails.documentTitle", { readerName });
  }, [readerName, t]);

  const emptyValue = (
    <Typography color="action.disabled" component="span">
      {t("management.devices.devices.empty.notAvailable")}
    </Typography>
  );
  const errorTitle =
    error === "reader"
      ? t("management.devices.rfid.readerDetails.errors.failedFetchReaderDetails")
      : error === "store"
        ? t("management.devices.rfid.readerDetails.errors.failedFetchStoreDetails")
        : t("management.devices.rfid.readerDetails.errors.unexpected");

  return (
    <PageShell
      breadcrumbs="admin"
      headerActions={
        reader && !isLoading ? (
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => void navigate(`${paths.admin.rfidReaders}/edit/${reader.name}`)}
          >
            {t("management.devices.rfid.readerDetails.actions.editReader")}
          </Button>
        ) : undefined
      }
      icon={<RouterIcon />}
      title={reader?.name ?? t("management.devices.rfid.readerDetails.title")}
      titleAccessory={<LoadingRing loading={isLoading} />}
    >
      {isLoading && (
        <Box sx={{ py: 8 }}>
          <LoadingState label={t("common.loading")} />
        </Box>
      )}

      {error && !isLoading && <ErrorState title={errorTitle} onRetry={() => void loadReader()} />}

      {reader && store && !isLoading && !error && (
        <Stack spacing={3} sx={{ mt: 2 }}>
          <AdminSectionPanel
            icon={<RouterIcon />}
            title={t("management.devices.rfid.readerDetails.sections.readerInformation")}
          >
            <Box component="dl" sx={{ ...detailsGridSx, m: 0 }}>
              <DetailItem
                label={t("management.devices.rfid.readerDetails.text.readerName")}
                value={reader.name}
              />
              <DetailItem label={t("management.devices.devices.fields.store")} value={store.name} />
              <DetailItem
                label={t("management.devices.rfid.readerDetails.text.externalId")}
                value={reader.externalId || emptyValue}
              />
              <DetailItem
                label={t("management.devices.rfid.common.fields.username")}
                value={reader.username || emptyValue}
              />
              <DetailItem
                label={t("management.devices.rfid.common.fields.password")}
                value={reader.password || emptyValue}
              />
              <DetailItem
                label={t("management.devices.rfid.readerDetails.text.rfidUrl")}
                value={
                  rfidUrl ? (
                    <Box
                      component="code"
                      sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                    >
                      {`${import.meta.env.VITE_API_URL}${rfidUrl}`}
                    </Box>
                  ) : (
                    t("management.devices.rfid.readerDetails.empty.none")
                  )
                }
              />
            </Box>
          </AdminSectionPanel>

          <AdminSectionPanel
            icon={<SettingsIcon />}
            title={t("management.devices.rfid.readerDetails.sections.readerSettings")}
          >
            <Box component="dl" sx={{ ...detailsGridSx, m: 0 }}>
              <DetailItem
                label={t("management.devices.rfid.readerDetails.text.debugMode")}
                value={
                  <Chip
                    color={reader.debug ? "success" : "default"}
                    icon={reader.debug ? <CheckIcon /> : <CloseIcon />}
                    label={t(
                      reader.debug
                        ? "management.devices.devices.status.enabled"
                        : "management.devices.devices.status.disabled",
                    )}
                    size="small"
                    variant="outlined"
                  />
                }
              />
              <DetailItem
                label={t("management.devices.rfid.common.fields.deduplication")}
                value={
                  <Chip
                    color={reader.enableDeduplication ? "success" : "default"}
                    icon={reader.enableDeduplication ? <CheckIcon /> : <CloseIcon />}
                    label={t(
                      reader.enableDeduplication
                        ? "management.devices.devices.status.enabled"
                        : "management.devices.devices.status.disabled",
                    )}
                    size="small"
                    variant="outlined"
                  />
                }
              />
            </Box>
          </AdminSectionPanel>

          <AdminTablePanel
            icon={<PortIcon />}
            title={t("management.devices.rfid.common.text.portConfiguration")}
            tableSx={{
              "& .MuiTableHead-root .MuiTableCell-root": {
                borderBottom: "1px solid",
                borderTop: "1px solid",
                borderColor: "divider",
              },
              "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
                borderInlineStart: (theme) => `1px solid ${theme.palette.divider}`,
                borderStartStartRadius: 0,
              },
              "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
                borderInlineEnd: (theme) => `1px solid ${theme.palette.divider}`,
                borderStartEndRadius: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="port"
                  direction={sortDirection}
                  onSort={handleSort}
                  scope="col"
                >
                  {t("management.devices.rfid.readerDetails.text.port")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="changingRoomId"
                  direction={sortDirection}
                  onSort={handleSort}
                  scope="col"
                >
                  {t("management.devices.rfid.readerDetails.text.changingRoomId")}
                </SortableTableCell>
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="rssiFilter"
                  direction={sortDirection}
                  onSort={handleSort}
                  scope="col"
                >
                  {t("management.devices.rfid.common.fields.rssiFilter")}
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reader.ports.length > 0 ? (
                sortedItems.map((port) => (
                  <TableRow key={port.port} hover>
                    <TableCell>{port.port}</TableCell>
                    <TableCell>{port.changingRoomId}</TableCell>
                    <TableCell>{port.rssiFilter}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className="table-empty-state">
                  <TableCell colSpan={3}>
                    <AdminEmptyState
                      icon={<PortIcon sx={{ fontSize: 40 }} />}
                      text={t("management.devices.rfid.readerDetails.empty.noPortsConfigured")}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </AdminTablePanel>
        </Stack>
      )}
    </PageShell>
  );
};

export default RFIDReaderDetails;
